/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import {
  EntityAction,
  EntityCollection,
  EntityCollectionReducerMethodMap,
  EntityCollectionReducerMethods,
  EntityDefinition,
  EntityDefinitionService,
  QueryParams,
} from '@ngrx/data';
import { DateTime } from 'luxon';
import { OperatorFunction } from 'rxjs';
import { filter } from 'rxjs/operators';

interface Versioned {
  version: number;
}

export class CcoEntityCollectionReducerMethods<T> extends EntityCollectionReducerMethods<T> {
  private correlationIdQueryMap: { [correlationId: string]: string } = {};
  // private correlationIdLastEvaluatedKeyKeyMap: { [correlationId: string]: string } = {};

  static isPayloadVersionNewerOrEqualRecursive<T>(
    existingEntity: T | undefined,
    payloadEntity: T | undefined,
  ): boolean {
    const currentVersion = (existingEntity as unknown as Versioned)?.version;

    if (currentVersion == null)
      return true;

    const newVersion = (payloadEntity as unknown as Versioned)?.version;
    if (newVersion == null)
      return false;

    if (newVersion > currentVersion) return true;
    if (newVersion < currentVersion) return false;

    for (const propertyName of Object.getOwnPropertyNames(existingEntity)) {
      const existingChild = existingEntity[propertyName];
      if (
        Object.prototype.toString.call(existingChild) === '[object Object]' &&
        existingChild.version != null &&
        this.isPayloadVersionNewerOrEqualRecursive(existingChild, payloadEntity[propertyName])
      ) {
        return true;
      }
    }

    return false;
  }

  constructor(public entityName: string, public definition: EntityDefinition<T>) {
    super(entityName, definition);
  }

  protected queryByKeySuccess(
    collection: EntityCollection<T>,
    action: EntityAction<T>,
  ): EntityCollection<T> {
    if (!this.isActionPayloadVersionNewerOrEqual(collection, action)) return collection;

    return super.queryByKeySuccess(collection, action);
  }

  protected queryLoadSuccess(
    collection: EntityCollection<T>,
    action: EntityAction<T[]>,
  ): EntityCollection<T> {
    // Ignore queryLoad for paged entities
    if (!(collection as any).paged) {
      const ec = super.queryLoadSuccess(collection, action);

      (ec as any).loadedAt = DateTime.local().toISO();

      return ec;
    }

    return collection;
  }

  protected queryMany(
    collection: EntityCollection<T>,
    action: EntityAction<QueryParams | string>,
  ): EntityCollection<T> {
    const ec = super.queryMany(collection, action);

    if (action.payload.correlationId) {
      this.correlationIdQueryMap[action.payload.correlationId] = typeof action.payload.data === 'string'
        ? action.payload.data
        : JSON.stringify(action.payload.data);

      // if ((action.payload as any).lastEvaluatedKeyKey) {
      //   this.correlationIdLastEvaluatedKeyKeyMap[
      //     action.payload.correlationId
      //   ] = (action.payload as any).lastEvaluatedKeyKey;
      // }
    }

    return ec;
  }

  protected queryManySuccess(
    collection: EntityCollection<T>,
    action: EntityAction<T[]>,
  ): EntityCollection<T> {
    const ec = super.queryManySuccess(collection, action);

    if ((ec as any).queryLoadedAt == null) {
      (ec as any).queryLoadedAt = {};
    }

    if (this.correlationIdQueryMap[action.payload.correlationId] != null) {
      (ec as any).queryLoadedAt[this.correlationIdQueryMap[action.payload.correlationId]] =
        DateTime.local().toISO();
      delete this.correlationIdQueryMap[action.payload.correlationId];
    }

    // Handle DynamoDB responses
    const lastEvaluatedKeyKey = (action.payload as any).lastEvaluatedKeyKey;
    if (lastEvaluatedKeyKey) {
      if ((ec as any).lastEvaluatedKeyMap == null) {
        (ec as any).lastEvaluatedKeyMap = {};
      }

      if ((action.payload as any).lastEvaluatedKey) {
        (ec as any).lastEvaluatedKeyMap[lastEvaluatedKeyKey] = (
          action.payload as any
        ).lastEvaluatedKey;
      }
      // delete this.correlationIdLastEvaluatedKeyKeyMap[action.payload.correlationId];
    }

    // Handle paged responses
    // Only set loadedAt when this is the last page of a getAllPages request
    if ((action.payload as any).pageInfo?.lastPageOfGetAll) {
      (ec as any).loadedAt = DateTime.local().toISO();
    }

    // Handle paged responses with global filters
    // Only set queryLoadedAt when the last page of a getWithQuery
    if ((action.payload as any).pageInfo?.lastPageOfGetWithQuery) {
      (ec as any).queryLoadedAt[(action.payload as any).pageInfo.query] = DateTime.local().toISO();
    }

    return ec;
  }

  protected removeAll(
    collection: EntityCollection<T>,
    action: EntityAction<T>,
  ): EntityCollection<T> {
    const ec = super.removeAll(collection, action);

    (ec as any).loadedAt = null;
    (ec as any).queryLoadedAt = {};

    return ec;
  }

  protected isActionPayloadVersionNewerOrEqual(
    collection: EntityCollection<T>,
    action: EntityAction<T>,
  ): boolean {
    const existingEntity = collection.entities[this.definition.selectId(action.payload.data)];
    const payloadEntity = action.payload.data;

    return CcoEntityCollectionReducerMethods.isPayloadVersionNewerOrEqualRecursive(existingEntity, payloadEntity);
  }
}

@Injectable()
export class CcoEntityCollectionReducerMethodsFactory {
  constructor(private entityDefinitionService: EntityDefinitionService) {}

  create<T>(entityName: string): EntityCollectionReducerMethodMap<T> {
    const definition = this.entityDefinitionService.getDefinition<T>(entityName);
    const methodsClass = new CcoEntityCollectionReducerMethods(entityName, definition);
    return methodsClass.methods;
  }
}

export function ofCorrelationId<T extends EntityAction>(
  correlationId: string,
): OperatorFunction<EntityAction, T> {
  return filter(
    (action: EntityAction): action is T =>
      action.payload && action.payload.correlationId === correlationId,
  );
}
